import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';

import { fetchPublicExperience, getFallbackPublicExperience } from '../services/publicExperience';

const PublicExperienceContext = createContext({
  experience: getFallbackPublicExperience(),
  experienceSource: 'fallback',
  loadingExperience: true,
});

const hexToRgbString = (value) => {
  const normalized = String(value || '').trim().replace('#', '');

  if (![3, 6].includes(normalized.length) || !/^[0-9a-fA-F]+$/.test(normalized)) {
    return null;
  }

  const expanded =
    normalized.length === 3
      ? normalized
          .split('')
          .map((character) => `${character}${character}`)
          .join('')
      : normalized;

  const red = Number.parseInt(expanded.slice(0, 2), 16);
  const green = Number.parseInt(expanded.slice(2, 4), 16);
  const blue = Number.parseInt(expanded.slice(4, 6), 16);

  return `${red}, ${green}, ${blue}`;
};

const darkenHex = (value, percentage = 0.18) => {
  const normalized = String(value || '').trim().replace('#', '');

  if (![3, 6].includes(normalized.length) || !/^[0-9a-fA-F]+$/.test(normalized)) {
    return null;
  }

  const expanded =
    normalized.length === 3
      ? normalized
          .split('')
          .map((character) => `${character}${character}`)
          .join('')
      : normalized;

  const factor = Math.max(0, 1 - percentage);
  const channels = [0, 2, 4].map((start) =>
    Math.max(0, Math.min(255, Math.round(Number.parseInt(expanded.slice(start, start + 2), 16) * factor))),
  );

  return `#${channels.map((channel) => channel.toString(16).padStart(2, '0')).join('')}`;
};

const upsertHeadLink = (rel, href) => {
  if (!href || typeof document === 'undefined') {
    return;
  }

  let element = document.querySelector(`link[rel="${rel}"]`);

  if (!element) {
    element = document.createElement('link');
    element.setAttribute('rel', rel);
    document.head.appendChild(element);
  }

  element.setAttribute('href', href);
};

const upsertHeadMeta = (name, content) => {
  if (!name || !content || typeof document === 'undefined') {
    return;
  }

  let element = document.querySelector(`meta[name="${name}"]`);

  if (!element) {
    element = document.createElement('meta');
    element.setAttribute('name', name);
    document.head.appendChild(element);
  }

  element.setAttribute('content', content);
};

const shouldUseLocalFavicon = (value) => {
  const normalized = String(value || '').trim();

  if (!normalized) {
    return true;
  }

  try {
    const referenceUrl = new URL(
      normalized,
      typeof window !== 'undefined' ? window.location.origin : 'https://localhost',
    );

    return referenceUrl.pathname.toLowerCase().endsWith('/favicon.ico');
  } catch (error) {
    return normalized.toLowerCase() === 'favicon.ico' || normalized.toLowerCase().endsWith('/favicon.ico');
  }
};

const applyExperienceBranding = (experience) => {
  if (typeof document === 'undefined') {
    return;
  }

  const tokens = experience?.data?.theme?.tokens || {};
  const branding = experience?.data?.branding || {};
  const root = document.documentElement;
  const brandRgb = hexToRgbString(tokens.colorPrimary);
  const darkerBrand = darkenHex(tokens.colorPrimary);

  if (tokens.colorPrimary) {
    root.style.setProperty('--brand', tokens.colorPrimary);
  }

  if (darkerBrand) {
    root.style.setProperty('--brand-700', darkerBrand);
  }

  if (brandRgb) {
    root.style.setProperty('--brand-rgb', brandRgb);
  }

  if (tokens.colorSurface) {
    root.style.setProperty('--bg', tokens.colorSurface);
    root.style.setProperty('--surface', tokens.colorSurface);
  }

  if (tokens.colorText) {
    root.style.setProperty('--ink', tokens.colorText);
  }

  if (tokens.radiusMd) {
    root.style.setProperty('--r-md', tokens.radiusMd);
  }

  if (tokens.radiusLg) {
    root.style.setProperty('--r-lg', tokens.radiusLg);
  }

  const faviconSrc = shouldUseLocalFavicon(branding?.favicon?.src)
    ? '/pwa/favicon-32x32.png'
    : branding?.favicon?.src;

  if (faviconSrc) {
    upsertHeadLink('icon', faviconSrc);
    upsertHeadLink('shortcut icon', faviconSrc);
  }

  upsertHeadLink('apple-touch-icon', '/pwa/apple-touch-icon.png');

  if (tokens.colorPrimary) {
    upsertHeadMeta('theme-color', tokens.colorPrimary);
  }
};

export function PublicExperienceProvider({ children }) {
  const [experience, setExperience] = useState(() => getFallbackPublicExperience());
  const [experienceSource, setExperienceSource] = useState('fallback');
  const [loadingExperience, setLoadingExperience] = useState(true);

  useEffect(() => {
    let active = true;

    async function loadExperience() {
      const result = await fetchPublicExperience();

      if (!active) {
        return;
      }

      setExperience(result.experience);
      setExperienceSource(result.source);
      setLoadingExperience(false);
    }

    loadExperience();

    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    applyExperienceBranding(experience);
  }, [experience]);

  const value = useMemo(
    () => ({
      experience,
      experienceSource,
      loadingExperience,
    }),
    [experience, experienceSource, loadingExperience],
  );

  return (
    <PublicExperienceContext.Provider value={value}>
      {children}
    </PublicExperienceContext.Provider>
  );
}

export const usePublicExperience = () => useContext(PublicExperienceContext);
